import React from 'react';
import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import parse from 'html-react-parser';

import { neutral, warning } from '_lex/_foundations/phxColors';
import { translate as t } from 'hooks/useTranslations';
import PolicyContentHelper from 'views/App/Portal/Policy/_helpers/policyContentHelper';
import type { ContentReference } from '../../../_types/dtb';
import { usePolicyReferenceResolver } from '../../_utils/policyReferenceUtils';

interface PolicyReferencePreviewProps {
  contentReferences: ContentReference[];
}

const PolicyReferencePreview: React.FC<PolicyReferencePreviewProps> = ({ contentReferences }) => {
  const { resolvedRefs, isLoading } = usePolicyReferenceResolver(contentReferences);

  if (isLoading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant='rectangular' height={20} width='40%' />
        <Skeleton variant='text' />
        <Skeleton variant='text' />
        <Skeleton variant='text' width='80%' />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        border: `1px solid ${neutral[200]}`,
        borderRadius: 2,
        p: 10,
        bgcolor: warning[25],
      }}
    >
      <Stack spacing={3}>
        <Typography variant='subtitleMd' fontWeight={600}>
          {t('dtbs.library.detail.policyReferenceBlock.title', 'Policy Reference')}
        </Typography>
        {contentReferences.length === 0 ? (
          <Typography variant='body1' color='text.secondary'>
            {t('dtbs.library.detail.policyReferenceBlock.noReferences', 'No policy references linked.')}
          </Typography>
        ) : (
          <Stack spacing={10} divider={<Divider />}>
            {resolvedRefs.map((ref, index) => (
              <Stack key={index} spacing={1}>
                <Typography variant='subtitleSm' fontWeight={600}>
                  {ref.name}
                </Typography>
                {ref.content && (
                  <Typography variant='body1' component='div'>
                    {parse(ref.content, {
                      replace(node: any, ind: number) {
                        return PolicyContentHelper.handleContent(node, ind);
                      },
                    })}
                  </Typography>
                )}
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

export default PolicyReferencePreview;
